import React, { useState } from 'react';
import { Box, Grid, Typography, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import { calculateAgesAndFlags } from '../../../../../v2/Utils/AgeCalc';
import { 
    TextField,
    CustomCheckbox,
    CustomButton,
    CustomSelect
} from '../../../../../v2/MUI_custom_component';
import { DatePicker } from '../../../../../v2/MUI_custom_component';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

const ManualEntryForm = ({ staffData, onSave, onCancel, initialData = {} }) => {
    const [formData, setFormData] = useState({
        full_name: initialData.full_name || "",
        dob: initialData.dob || "",
        age: initialData.age || "",
        gender: initialData.gender || "MALE",
        phone_number: initialData.phone_number || ""
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [confirm, setConfirm] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const { [field]: removed, ...rest } = prev;
                return rest;
            });
        }

        // Auto-calculate age when DOB changes
        if (field === 'dob' && value) {
            const today = dayjs();
            const birthDate = dayjs(value);
            
            if (birthDate.isValid()) {
                const age = today.diff(birthDate, 'year');
                
                if (age >= 0) {
                    setFormData(prev => ({
                        ...prev,
                        age: age.toString()
                    }));
                }
            }
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.full_name.trim()) {
            errors.full_name = "Full name is required";
        }

        if (!formData.dob) {
            errors.dob = "Date of birth is required";
        }

        // Age validation for beneficiaries
        if (formData.dob) {
            const ageResult = calculateAgesAndFlags([formData.dob]);
            if (ageResult[0]?.ageFlag) {
                const age = parseInt(formData.age);
                if (age < 18) {
                    errors.age = 'Beneficiary must be at least 18 years old';
                } else if (age > 60) {
                    errors.age = 'Beneficiary must be under 60 years old';
                }
            }
        }

        if (!formData.gender) {
            errors.gender = "Gender is required";
        }

        if (!formData.phone_number.trim()) {
            errors.phone_number = "Phone number is required";
        } else if (!/^\d{10}$/.test(formData.phone_number.replace(/\D/g, ''))) {
            errors.phone_number = "Please enter a valid 10-digit phone number";
        }

        if (!confirm) {
            errors.confirm = "Please confirm the details to proceed";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        onSave({
            ...formData,
            beneficiary_id: Date.now(),
            staff_no: staffData.staff_no,
            ocr_verified: false // Manual entry, not OCR verified
        });
    };

    return (
        <Box>
            <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Grid item xs={12}>
                    <TextField
                        label="Full Name*"
                        value={formData.full_name}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        error={!!validationErrors.full_name}
                        helperText={validationErrors.full_name}
                        placeholder="Enter full name"
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date of Birth*"
                            value={formData.dob ? dayjs(formData.dob) : null}
                            onChange={(newValue) => {
                                const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                                handleInputChange('dob', dateString);
                            }}
                            error={!!validationErrors.dob}
                            helperText={validationErrors.dob}
                            disableFuture
                            format="DD/MM/YYYY"
                            fullWidth
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Age"
                        value={formData.age}
                        onChange={(e) => handleInputChange('age', e.target.value)}
                        error={!!validationErrors.age}
                        helperText={validationErrors.age}
                        placeholder="Age"
                        inputProps={{ min: 0, max: 120 }}
                        fullWidth
                        readOnly
                    />
                </Grid>

                <Grid item xs={12}>
                    <CustomSelect
                        id="gender-select"
                        label="Gender*"
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        error={!!validationErrors.gender}
                        helperText={validationErrors.gender}
                        displayEmpty
                        fullWidth
                    >
                        <MenuItem value="" disabled>
                            Choose gender
                        </MenuItem>
                        <MenuItem value="MALE">Male</MenuItem>
                        <MenuItem value="FEMALE">Female</MenuItem>
                    </CustomSelect>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Phone Number*"
                        value={formData.phone_number}
                        onChange={(e) => handleInputChange('phone_number', e.target.value)}
                        error={!!validationErrors.phone_number}
                        helperText={validationErrors.phone_number}
                        placeholder="Enter 10-digit phone number"
                        inputProps={{ maxLength: 10 }}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                    <CustomCheckbox 
                        label="Details provided herein are true and I have obtained all relevant organisational consents for the purposes."
                        checked={confirm}
                        onChange={(e) => {
                            setConfirm(e.target.checked);
                            if (e.target.checked && validationErrors.confirm) {
                                setValidationErrors(prev => {
                                    const { confirm, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                    />
                    {validationErrors.confirm && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {validationErrors.confirm}
                        </Typography>
                    )}
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <CustomButton
                    variant="filled"
                    onClick={handleSubmit}
                    label="Save & Continue"
                    fullWidth
                />
            </Box>
        </Box>
    );
};

export default ManualEntryForm;